import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { financeService, type ReimbursementCategory } from '@/services/finance.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

interface FormValues {
  name: string;
  monthly_limit: string;
  per_claim_limit: string;
  bill_required_above: string;
  gst_applicable: boolean;
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  category?: ReimbursementCategory;
  onClose: () => void;
  onSuccess: () => void;
}

export function ReimbursementCategoryDialog({ open, mode, category, onClose, onSuccess }: Props) {
  const isEdit = mode === 'edit';
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    defaultValues: { name: '', monthly_limit: '', per_claim_limit: '', bill_required_above: '', gst_applicable: false },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: category?.name ?? '',
        monthly_limit: category ? String(Number(category.monthly_limit)) : '',
        per_claim_limit: category ? String(Number(category.per_claim_limit)) : '',
        bill_required_above: category ? String(Number(category.bill_required_above)) : '',
        gst_applicable: category?.gst_applicable ?? false,
      });
    }
  }, [open, category, reset]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const input = {
        name: values.name.trim(),
        monthly_limit: Number(values.monthly_limit) || 0,
        per_claim_limit: Number(values.per_claim_limit) || 0,
        bill_required_above: Number(values.bill_required_above) || 0,
        gst_applicable: values.gst_applicable,
      };
      if (isEdit && category) await financeService.updateReimbursementCategory(category.id, input);
      else await financeService.createReimbursementCategory(input);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Category updated' : 'Category created');
      void queryClient.invalidateQueries({ queryKey: ['finance', 'reimbursement', 'categories'] });
      void queryClient.invalidateQueries({ queryKey: ['finance', 'reimbursement', 'budget'] });
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Category' : 'Add Category'}</DialogTitle>
          <DialogDescription>Configure expense category limits and rules</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="rc-name">Name *</Label>
            <Input id="rc-name" {...register('name', { required: 'Name is required' })} />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rc-month">Monthly Limit (₹)</Label>
            <Input id="rc-month" type="number" min="0" step="0.01" {...register('monthly_limit')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rc-claim">Per Claim Limit (₹)</Label>
            <Input id="rc-claim" type="number" min="0" step="0.01" {...register('per_claim_limit')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rc-bill">Bill Required Above (₹)</Label>
            <Input id="rc-bill" type="number" min="0" step="0.01" {...register('bill_required_above')} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" {...register('gst_applicable')} className="h-4 w-4 rounded border-input" />
            GST Applicable
          </label>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
